import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { showError, showSuccess } from "../../utils/notifications";
import { logError } from "../../utils/errorHandling";
import {
  listSchools,
  listDistricts,
  createSchool,
  createDistrict as createDistrictApi,
  updateDistrict as updateDistrictApi,
  updateSchool,
} from "../../api/schoolConfigApi";
import { listUsers } from "../../api/adminUsersApi";
import AdminPortalWrapper from "./AdminPortalWrapper";
import LoadingScreen from "../common/LoadingScreen";
import type { components } from "../../api/types.gen";
import styles from "./DeveloperDashboard.module.css";

type SchoolResponse = components["schemas"]["SchoolResponse"];
type DistrictResponse = components["schemas"]["DistrictResponse"];
type UserResponse = components["schemas"]["UserResponse"];

type DashboardTab = "organizations" | "districts" | "users" | "admin";

/**
 * Fields collected from the create-organization modal form before they are
 * dispatched to the school/district create endpoints.
 */
interface CreateOrganizationInput {
  name: string;
  type: string;
  districtId: number | null;
}

/**
 * A legacy Firebase-era timestamp shape: `createdAt` is now an ISO string on
 * the REST responses, but formatDate still tolerates a Firestore Timestamp's
 * `.toDate()` (and a raw number/Date) so its runtime behavior is unchanged.
 */
type MaybeFirestoreTs =
  | string
  | number
  | Date
  | { toDate?: () => Date }
  | null
  | undefined;

const DeveloperDashboard = (): React.JSX.Element => {
  const { userRole, logout } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<SchoolResponse[]>([]);
  const [districts, setDistricts] = useState<DistrictResponse[]>([]);
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<DashboardTab>("organizations");
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showCreateDistrictModal, setShowCreateDistrictModal] = useState(false);
  const [editingDistrict, setEditingDistrict] =
    useState<DistrictResponse | null>(null);

  // Check if user is a developer
  useEffect(() => {
    if (userRole !== "developer") {
      showError("Access denied. Developer role required.");
      navigate("/");
    }
  }, [userRole, navigate]);

  // Load all data
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);

      // Load organizations (schools). Note: hallpass-backend has no
      // billing/emailWhitelist concept on School, so those fields are
      // simply absent here (render code already treats them as optional).
      const schoolsResult = await listSchools({ limit: 100 });
      setOrganizations([...schoolsResult.data].sort((a, b) => a.id - b.id));

      // Load districts. District.schools is FK-derived (School.districtId),
      // not a stored array, so it is not present on this response.
      const districtsResult = await listDistricts({ limit: 100 });
      setDistricts([...districtsResult.data].sort((a, b) => a.id - b.id));

      // Load users (limited to first 100 for performance)
      const usersResult = await listUsers({ limit: 100 });
      setUsers(usersResult.data);
    } catch (error) {
      logError(error, "developer_dashboard", "Failed to load data");
      showError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const createOrganization = async (formData: CreateOrganizationInput) => {
    try {
      const isDistrict = formData.type === "district";
      const created = isDistrict
        ? await createDistrictApi({ name: formData.name })
        : await createSchool({
            name: formData.name,
            ...(formData.districtId ? { districtId: formData.districtId } : {}),
          });

      showSuccess(
        `${isDistrict ? "District" : "School"} created with ID: ${created.id}`,
      );
      setShowCreateOrgModal(false);
      await loadAllData();
    } catch (error) {
      logError(error, "create_org", "Failed to create organization");
      showError("Failed to create organization");
    }
  };

  const createDistrict = async (formData: { name: string }) => {
    try {
      const created = await createDistrictApi({ name: formData.name });

      showSuccess(`District "${formData.name}" created with ID: ${created.id}`);
      setShowCreateDistrictModal(false);
      await loadAllData();
    } catch (error) {
      logError(error, "create_district", "Failed to create district");
      showError("Failed to create district");
    }
  };

  const linkOrgToDistrict = async (orgId: number, districtId: number) => {
    try {
      // Update organization with districtId
      await updateSchool(orgId, { districtId });

      showSuccess(`Organization ${orgId} linked to District ${districtId}`);
      await loadAllData();
    } catch (error) {
      logError(
        error,
        "link_org_district",
        "Failed to link organization to district",
      );
      showError("Failed to link organization to district");
    }
  };

  const formatDate = (timestamp: MaybeFirestoreTs) => {
    if (!timestamp) return "N/A";
    const date =
      typeof timestamp === "object" &&
      "toDate" in timestamp &&
      typeof timestamp.toDate === "function"
        ? timestamp.toDate()
        : new Date(timestamp as string | number | Date);
    return date.toLocaleDateString();
  };

  if (loading) {
    return <LoadingScreen message="Loading developer dashboard..." />;
  }

  return (
    <div className={styles["developer-dashboard"]}>
      <div className={styles["dashboard-header"]}>
        <h1>System Developer Dashboard</h1>
        <div className={styles["header-actions"]}>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLScdXhb7Qs0IOmSG3UoMLMqOP1CBpoSKFbdAe6iExkyrenIGqg/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className={styles["bug-report-link"]}
          >
            🐛 Report Bug
          </a>
          <button className={styles["logout-btn"]} onClick={logout}>
            Sign Out
          </button>
        </div>
      </div>

      <div className={styles["stats-overview"]}>
        <div className={styles["stat-card"]}>
          <h3>Total Organizations</h3>
          <p className={styles["stat-number"]}>{organizations.length}</p>
        </div>
        <div className={styles["stat-card"]}>
          <h3>Total Districts</h3>
          <p className={styles["stat-number"]}>{districts.length}</p>
        </div>
        <div className={styles["stat-card"]}>
          <h3>Total Users</h3>
          <p className={styles["stat-number"]}>{users.length}</p>
        </div>
      </div>

      <div className={styles["dashboard-tabs"]}>
        <button
          className={`${styles["tab"]} ${activeTab === "organizations" ? styles["active"] : ""}`}
          onClick={() => setActiveTab("organizations")}
        >
          Organizations
        </button>
        <button
          className={`${styles["tab"]} ${activeTab === "districts" ? styles["active"] : ""}`}
          onClick={() => setActiveTab("districts")}
        >
          Districts
        </button>
        <button
          className={`${styles["tab"]} ${activeTab === "users" ? styles["active"] : ""}`}
          onClick={() => setActiveTab("users")}
        >
          Users
        </button>
        <button
          className={`${styles["tab"]} ${activeTab === "admin" ? styles["active"] : ""}`}
          onClick={() => setActiveTab("admin")}
        >
          Admin Portal
        </button>
      </div>

      {activeTab === "organizations" && (
        <div className={styles["tab-content"]}>
          <div className={styles["content-header"]}>
            <h2>Organizations (First 100)</h2>
            <button
              className={styles["create-btn"]}
              onClick={() => setShowCreateOrgModal(true)}
            >
              + Create Organization
            </button>
          </div>

          <div className={styles["data-table"]}>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>District</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => (
                  <tr key={org.id}>
                    <td>{org.id}</td>
                    <td>{org.name}</td>
                    <td>
                      {org.districtId
                        ? districts.find((d) => d.id === org.districtId)
                            ?.name || `District ${org.districtId}`
                        : "None"}
                    </td>
                    <td>
                      {(org as SchoolResponse & { type?: string }).type ||
                        "school"}
                    </td>
                    <td>
                      {!org.districtId && districts.length > 0 && (
                        <button
                          className={styles["action-btn"]}
                          onClick={() => {
                            const raw = prompt(
                              `Link ${org.name} to which district ID?`,
                              "1",
                            );
                            if (raw === null) return; // user cancelled
                            const districtId = parseInt(raw, 10);
                            if (
                              !Number.isInteger(districtId) ||
                              districtId <= 0
                            ) {
                              showError(
                                "Please enter a valid district ID (a positive number).",
                              );
                              return;
                            }
                            linkOrgToDistrict(org.id, districtId);
                          }}
                          style={{ marginLeft: "8px" }}
                        >
                          Link to District
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "districts" && (
        <div className={styles["tab-content"]}>
          <div className={styles["content-header"]}>
            <h2>Districts (First 100)</h2>
            <button
              className={styles["create-btn"]}
              onClick={() => setShowCreateDistrictModal(true)}
            >
              + Create District
            </button>
          </div>

          <div className={styles["data-table"]}>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Schools</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {districts.map((district) => (
                  <tr key={district.id}>
                    <td>{district.id}</td>
                    <td>{district.name}</td>
                    <td>
                      {
                        organizations.filter(
                          (o) => o.districtId === district.id,
                        ).length
                      }
                    </td>
                    <td>
                      <button
                        className={styles["action-btn"]}
                        onClick={() => setEditingDistrict(district)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div className={styles["tab-content"]}>
          <div className={styles["content-header"]}>
            <h2>Users (First 100)</h2>
          </div>

          <div className={styles["data-table"]}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Organizations</th>
                  <th>Roles</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name || "N/A"}</td>
                    <td>{user.email}</td>
                    <td>
                      {user.schoolId
                        ? organizations.find((o) => o.id === user.schoolId)
                            ?.name || `Org ${user.schoolId}`
                        : "None"}
                    </td>
                    <td>
                      {user.role === "SUPER_ADMIN"
                        ? "Super Admin"
                        : user.role || "None"}
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "admin" && (
        <div className={styles["tab-content"]}>
          <AdminPortalWrapper />
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateOrgModal && (
        <div
          className={styles["modal-overlay"]}
          onClick={() => setShowCreateOrgModal(false)}
        >
          <div className={styles["modal"]} onClick={(e) => e.stopPropagation()}>
            <h2>Create Organization</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                createOrganization({
                  name: String(formData.get("name")),
                  type: String(formData.get("type")),
                  districtId: formData.get("districtId")
                    ? parseInt(String(formData.get("districtId")))
                    : null,
                });
              }}
            >
              <label>
                Organization Name
                <input name="name" placeholder="Organization Name" required />
              </label>

              <label>
                Organization Type
                <select name="type" required>
                  <option value="school">School</option>
                  <option value="district">District</option>
                </select>
              </label>

              <label>
                District
                <select name="districtId">
                  <option value="">No District</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>

              <div className={styles["modal-actions"]}>
                <button type="submit" className={styles["save-btn"]}>
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateOrgModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create District Modal */}
      {showCreateDistrictModal && (
        <div
          className={styles["modal-overlay"]}
          onClick={() => setShowCreateDistrictModal(false)}
        >
          <div className={styles["modal"]} onClick={(e) => e.stopPropagation()}>
            <h2>Create District</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                createDistrict({
                  name: String(formData.get("name")),
                });
              }}
            >
              <label>
                District Name
                <input name="name" placeholder="District Name" required />
              </label>

              <div className={styles["modal-actions"]}>
                <button type="submit" className={styles["save-btn"]}>
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateDistrictModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit District Modal */}
      {editingDistrict && (
        <div
          className={styles["modal-overlay"]}
          onClick={() => setEditingDistrict(null)}
        >
          <div className={styles["modal"]} onClick={(e) => e.stopPropagation()}>
            <h2>Edit District: {editingDistrict.name}</h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);

                try {
                  await updateDistrictApi(editingDistrict.id, {
                    name: String(formData.get("name")),
                  });

                  showSuccess(
                    `District ${editingDistrict.name} updated successfully`,
                  );
                  setEditingDistrict(null);
                  await loadAllData();
                } catch (error) {
                  logError(
                    error,
                    "update_district",
                    "Failed to update district",
                  );
                  showError("Failed to update district");
                }
              }}
            >
              <label>
                District Name
                <input
                  name="name"
                  placeholder="District Name"
                  defaultValue={editingDistrict.name}
                  required
                />
              </label>

              <div className={styles["modal-actions"]}>
                <button type="submit" className={styles["save-btn"]}>
                  Save
                </button>
                <button type="button" onClick={() => setEditingDistrict(null)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperDashboard;
